import { TopBar, Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { PageMeta, BreadcrumbJsonLd, FAQJsonLd } from '@/components/PageMeta';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Heart, Shield, Users, TrendingUp } from 'lucide-react';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { Helmet } from 'react-helmet-async';

const debutantFaqs = [
  { q: "Le CrossFit est-il dangereux pour les débutants ?", a: "Non. Chez CrossFit Saint-Chamond, chaque mouvement est adapté (scalé) à votre niveau par un coach certifié. Les débutants commencent avec des charges légères et des mouvements simplifiés. La sécurité est notre priorité." },
  { q: "Faut-il être en forme pour commencer le CrossFit ?", a: "Pas du tout ! Le CrossFit est justement conçu pour améliorer votre forme physique. Nos coachs adaptent chaque exercice à votre niveau actuel. Plus de 60% de nos membres ont commencé sans aucune expérience sportive." },
  { q: "Combien de fois par semaine dois-je m'entraîner ?", a: "Pour un débutant, nous recommandons 2 à 3 séances par semaine pour laisser le temps au corps de récupérer. Vous pourrez augmenter la fréquence progressivement. Nos abonnements 2x/semaine (69€) ou 3x/semaine (89€) sont idéaux." },
  { q: "Quel équipement dois-je apporter ?", a: "Juste une tenue de sport, des chaussures plates (type training) et une serviette. Tout l'équipement CrossFit est fourni : barres, poids, rameurs, cordes, etc." },
  { q: "Combien de temps dure une séance ?", a: "Une séance dure environ 60 minutes : 10 min d'échauffement, 15 min de technique, 15-25 min de WOD (entraînement) et 10 min de retour au calme." },
];

export default function CrossFitDebutantPage() {
  const [heroRef, heroVisible] = useScrollReveal(0.1);
  const [contentRef, contentVisible] = useScrollReveal(0.1);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "CrossFit débutant : comment commencer à Saint-Chamond",
    "description": "Guide pour débuter le CrossFit à Saint-Chamond. Conseils, programme débutant, premier cours, tarifs. CrossFit accessible à tous dans la Loire.",
    "author": { "@type": "Person", "name": "Nicolas Perroy" },
    "publisher": { "@type": "Organization", "name": "CrossFit Saint-Chamond" },
    "datePublished": "2025-02-01",
    "dateModified": "2025-03-30"
  };

  return (
    <div className="min-h-screen">
      <PageMeta
        title="CrossFit débutant : comment commencer"
        description="Débuter le CrossFit à Saint-Chamond : guide complet pour débutants. Programme adapté, coachs certifiés, tous niveaux. CrossFit Saint-Chamond, Loire (42). Essai gratuit."
        path="/crossfit-debutant"
      />
      <BreadcrumbJsonLd items={[
        { name: 'Accueil', url: '/' },
        { name: 'CrossFit débutant' }
      ]} />
      <FAQJsonLd faqs={debutantFaqs} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <TopBar />
      <Navigation />

      {/* Hero */}
      <section ref={heroRef} className="relative bg-dark overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_063f6b75-858e-4561-abbf-3c8b3baa3d11/artifacts/egyxn2i5_WhatsApp%20Image%202026-04-02%20at%2014.37.45%20%281%29.jpeg" alt="CrossFit débutant - premier cours" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-dark via-dark/80 to-dark/50" />
        </div>
        <div className="container-custom relative z-10 py-16 md:py-24">
          <div className={`max-w-3xl transition-all duration-700 ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="flex items-center gap-2 text-white/50 text-sm mb-4">
              <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
              <span>/</span>
              <Link to="/guide-crossfit" className="hover:text-primary transition-colors">Guide CrossFit</Link>
              <span>/</span>
              <span className="text-white/70">Débutant</span>
            </div>
            <h1 className="font-display text-4xl md:text-6xl text-white leading-[0.9] mb-6">
              CROSSFIT <span className="text-primary">DÉBUTANT</span>
            </h1>
            <p className="text-white/60 text-lg max-w-xl">
              Vous n'avez jamais fait de CrossFit ? Pas de panique. Voici tout ce que vous devez savoir pour commencer à Saint-Chamond.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <article ref={contentRef} className="section-padding bg-white">
        <div className="container-custom max-w-4xl">
          <div className={`transition-all duration-700 ${contentVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>

            <h2 className="font-heading text-3xl text-dark mb-6">LE CROSSFIT EST FAIT POUR VOUS</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Beaucoup de gens pensent que le <strong>CrossFit</strong> est réservé aux athlètes ou aux sportifs confirmés. C'est faux. Le CrossFit est la méthode d'entraînement la plus <strong>accessible</strong> qui existe, car <strong>chaque exercice est adaptable</strong> (on dit "scalable") à votre niveau.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Chez <strong>CrossFit Saint-Chamond</strong>, nous accueillons chaque semaine des <strong>débutants complets</strong> : des personnes qui n'ont jamais mis les pieds dans une salle de sport. Nos coachs certifiés, guidés par <strong>Nicolas Perroy</strong>, prennent le temps de vous enseigner chaque mouvement en toute sécurité.
            </p>

            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              {[
                { icon: Shield, title: 'Sécurité d\'abord', desc: 'Chaque mouvement est enseigné progressivement. Vous ne soulevez jamais plus que ce que vous pouvez.' },
                { icon: Users, title: 'Communauté bienveillante', desc: 'Chez nous, pas de jugement. Tout le monde encourage tout le monde, du débutant au plus expérimenté.' },
                { icon: TrendingUp, title: 'Progression garantie', desc: 'Avec 2 à 3 séances par semaine, vous verrez des résultats dès les premières semaines.' },
                { icon: Heart, title: 'Plaisir avant tout', desc: 'Le WOD change chaque jour : pas de routine, pas d\'ennui. Vous aurez hâte de revenir.' },
              ].map(({ icon: Icon, title, desc }, i) => (
                <div key={i} className="bg-background p-6 rounded-xl">
                  <Icon className="h-6 w-6 text-primary mb-3" />
                  <h3 className="font-heading text-base text-dark mb-2">{title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{desc}</p>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-3xl text-dark mb-6">VOTRE PREMIER COURS À CROSSFIT SAINT-CHAMOND</h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Votre <strong>premier cours</strong> se déroule comme n'importe quelle séance, mais le coach prend un soin particulier à vous expliquer chaque mouvement. Voici comment ça se passe :
            </p>
            <div className="space-y-4 mb-12">
              {[
                { step: '1', title: 'Échauffement (10 min)', desc: 'Mobilité articulaire et activation musculaire douce. Le coach vérifie vos éventuelles restrictions de mouvement.' },
                { step: '2', title: 'Technique (15 min)', desc: 'Apprentissage des mouvements du jour. Le coach démontre, explique et corrige votre posture avec patience.' },
                { step: '3', title: 'WOD adapté (15-25 min)', desc: 'Le Workout Of the Day, adapté à votre niveau. Charges légères, mouvements simplifiés, à votre rythme.' },
                { step: '4', title: 'Retour au calme (10 min)', desc: 'Étirements et mobilité. Le coach répond à vos questions et vous donne des conseils pour la suite.' },
              ].map((s) => (
                <div key={s.step} className="flex gap-4 items-start">
                  <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">{s.step}</span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-dark text-sm mb-1">{s.title}</h3>
                    <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-3xl text-dark mb-6">QUESTIONS FRÉQUENTES — CROSSFIT DÉBUTANT</h2>
            <div className="space-y-4 mb-12">
              {debutantFaqs.map((faq, i) => (
                <div key={i} className="bg-background rounded-xl p-6">
                  <h3 className="font-semibold text-dark mb-2">{faq.q}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>

            <h2 className="font-heading text-3xl text-dark mb-6">ACCESSIBLE DEPUIS TOUTE LA LOIRE</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              <strong>CrossFit Saint-Chamond</strong> est situé au <strong>7 Rue Jean-Jacques Rousseau, 42400 Saint-Chamond</strong>, facilement accessible depuis <strong>Saint-Étienne</strong> (12 km), <strong>L'Horme</strong>, <strong>Rive-de-Gier</strong>, <strong>La Grand-Croix</strong>, <strong>Lorette</strong>, <strong>Firminy</strong>, <strong>La Talaudière</strong>, <strong>Sorbiers</strong> et toute la <strong>Vallée du Gier</strong>. Nos abonnements démarrent à <strong>59€/mois sans engagement</strong>.
            </p>
          </div>

          {/* CTA */}
          <div className="bg-primary rounded-2xl p-8 md:p-12 text-center">
            <h2 className="font-heading text-3xl text-white mb-4">VOTRE PREMIER COURS VOUS ATTEND</h2>
            <p className="text-white/80 mb-8 max-w-lg mx-auto">
              Contactez-nous pour réserver votre essai à CrossFit Saint-Chamond. C'est le premier pas vers une nouvelle version de vous.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white hover:bg-white/90 text-primary font-heading tracking-wide px-10 py-7 rounded-xl">
                RÉSERVER MON ESSAI <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>

          {/* Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap gap-3">
              {[
                { to: '/guide-crossfit', label: 'Guide du CrossFit' },
                { to: '/programmes/crossfit-wod', label: 'CrossFit WOD' },
                { to: '/programmes/hyrox', label: 'HYROX' },
                { to: '/planning', label: 'Planning des cours' },
                { to: '/tarifs', label: 'Nos tarifs' },
                { to: '/contact', label: 'Contact' },
              ].map((l) => (
                <Link key={l.to} to={l.to} className="text-xs bg-background hover:bg-accent px-3 py-1.5 rounded-lg transition-colors">
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
}
